import React, { useEffect, useState } from 'react';
import { LeaveApplicationResponse, LeaveStatus, UserLeaveBalanceSummary } from '../../interfaces/leave.interface';
import { LeaveService } from '../../services/leave.service';
import { LeaveBalanceService } from '../../services/leave-balance.service';
import {
    Box,
    Button,
    Card,
    CardContent,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Link,
    Tooltip
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { format } from 'date-fns';
import LeaveForm from './LeaveForm';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';

const LeaveApplicationList: React.FC = () => {
    const [leaveData, setLeaveData] = useState<LeaveApplicationResponse | null>(null);
    const [balanceSummary, setBalanceSummary] = useState<UserLeaveBalanceSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const toast = useToast();
    const { t } = useTranslation(['leave', 'common']);
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const [selectedLeaveId, setSelectedLeaveId] = useState<string | null>(null);

    useEffect(() => {
        fetchLeaveApplications();
        fetchBalance();
    }, []);

    const fetchLeaveApplications = async () => {
        try {
            const data = await LeaveService.getLeaveApplications();
            setLeaveData(data);
        } catch (err) {
            toast.showApiError(err, t('leaveApplicationList.errors.fetch'));
        } finally {
            setLoading(false);
        }
    };

    const fetchBalance = async () => {
        try {
            const data = await LeaveBalanceService.getMyBalance();
            setBalanceSummary(data);
        } catch (err) {
            // Silently fail - balance display is optional
        }
    };

    const handleApplyLeave = () => {
        setIsFormOpen(true);
    };

    const handleCancelClick = (id: string) => {
        setSelectedLeaveId(id);
        setCancelConfirmOpen(true);
    };

    const handleCancelConfirm = async () => {
        if (!selectedLeaveId) return;

        try {
            await LeaveService.cancelLeaveApplication(selectedLeaveId);
            await fetchLeaveApplications();
            setCancelConfirmOpen(false);
        } catch (err) {
            toast.showApiError(err, t('leaveApplicationList.errors.cancel'));
        }
    };

    const handleCancelDialogClose = () => {
        setCancelConfirmOpen(false);
        setSelectedLeaveId(null);
    };

    const getStatusColor = (status: LeaveStatus) => {
        switch (status) {
            case LeaveStatus.Approved:
                return 'success';
            case LeaveStatus.Rejected:
                return 'error';
            case LeaveStatus.Cancelled:
                return 'warning';
            default:
                return 'default';
        }
    };

    const formatDateTime = (dateString: string) => {
        return format(new Date(dateString), 'dd-MM-yyyy');
    };

    if (loading) return <Typography>{t('loading')}</Typography>;
    if (!leaveData) return <Typography>{t('leaveApplicationList.empty')}</Typography>;

    return (
        <Box sx={{ padding: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                <Typography variant="h5">
                    {t('leaveApplicationList.title')}
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleApplyLeave}
                >
                    {t('leaveApplicationList.applyLeave')}
                </Button>
            </Box>

            {balanceSummary && balanceSummary.balances.length > 0 && (
                <Box sx={{ display: 'flex', gap: 2, marginBottom: 3, flexWrap: 'wrap' }}>
                    {balanceSummary.balances.map((balance) => (
                        <Card key={balance.leaveType} sx={{ minWidth: 200, flex: '1 1 200px' }}>
                            <CardContent>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    {balance.leaveType}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h6">{balance.allocated}</Typography>
                                        <Typography variant="caption" color="text.secondary">{t('leaveApplicationList.allocated')}</Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h6">{balance.availed}</Typography>
                                        <Typography variant="caption" color="text.secondary">{t('leaveApplicationList.used')}</Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h6" color={balance.remaining < 0 ? 'error' : 'success.main'}>
                                            {balance.remaining}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">{t('leaveApplicationList.remaining')}</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('leaveApplicationList.columns.leaveType')}</TableCell>
                            <TableCell>{t('leaveApplicationList.columns.reason')}</TableCell>
                            <TableCell>{t('leaveApplicationList.columns.applicationDate')}</TableCell>
                            <TableCell>{t('leaveApplicationList.columns.startDate')}</TableCell>
                            <TableCell>{t('leaveApplicationList.columns.endDate')}</TableCell>
                            <TableCell>{t('leaveApplicationList.columns.days')}</TableCell>
                            <TableCell>{t('leaveApplicationList.columns.status')}</TableCell>
                            <TableCell>{t('leaveApplicationList.columns.approver')}</TableCell>
                            <TableCell>{t('leaveApplicationList.columns.documents')}</TableCell>
                            <TableCell>{t('leaveApplicationList.columns.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {leaveData.content.map((leave) => (
                            <TableRow key={leave.id}>
                                <TableCell>{leave.leaveType}</TableCell>
                                <TableCell sx={{ maxWidth: 260 }}>
                                    <Tooltip title={leave.leaveReason || ''} placement="top-start" arrow>
                                        <Box
                                            sx={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {leave.leaveReason}
                                        </Box>
                                    </Tooltip>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(leave.applicationDate)}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(leave.startDate)}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(leave.endDate)}</TableCell>
                                <TableCell>{leave.numberOfDays}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={t(`status.${leave.leaveStatus}`)}
                                        color={getStatusColor(leave.leaveStatus) as any}
                                        size="small"
                                        sx={{ pointerEvents: 'none' }}
                                    />
                                </TableCell>
                                <TableCell>
                                    {leave.actionedByName ? (
                                        <Box>
                                            <Typography variant="body2">{leave.actionedByName}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {leave.actionedAt ? format(new Date(leave.actionedAt), 'dd MMM yyyy, hh:mm a') : ''}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {leave.documents && leave.documents.length > 0 ? (
                                        <Box>
                                            {leave.documents.map((doc) => (
                                                <Link
                                                    key={doc.id}
                                                    component="button"
                                                    variant="body2"
                                                    onClick={() => LeaveService.downloadDocument(doc.id, doc.originalFileName)}
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
                                                >
                                                    <AttachFileIcon fontSize="small" />
                                                    {doc.originalFileName}
                                                </Link>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">-</Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={() => handleCancelClick(leave.id)}
                                        disabled={leave.leaveStatus !== LeaveStatus.Pending}
                                    >
                                        {t('leaveApplicationList.cancel')}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <LeaveForm
                open={isFormOpen}
                onClose={() => setIsFormOpen(false)}
                onSubmitSuccess={fetchLeaveApplications}
            />

            <Dialog
                open={cancelConfirmOpen}
                onClose={handleCancelDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('leaveApplicationList.cancelDialogTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('leaveApplicationList.cancelDialogMessage')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDialogClose}>{t('leaveApplicationList.no')}</Button>
                    <Button
                        onClick={handleCancelConfirm}
                        variant="contained"
                        color="error"
                        autoFocus
                    >
                        {t('leaveApplicationList.yesCancel')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LeaveApplicationList;
